import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader, Table, Group, Menu } from '@mantine/core';
import Header from '../../../components/util/header';
import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { formatCurrAmount } from '../../../utils/script';
import { ICurrency } from '../../../../libraries/currencies';
import { detectContractState } from '../../utils';
import { formatDateTimeLabel } from '../../../utils/date';
import { fetchHelpdeskContracts, IHelpdeskContract } from '../../../../libraries/helpdesk/contracts';
import PaginationComponent from '../../../components/pagination';
import HelpdeskFilterBar, { HelpdeskFilterBarFieldsData } from '../../components/helpdesk-filterbar';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../../context/status-oper-dialog';
import './styles.scss';

const HelpdeskContracts: React.FC = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');
  const controller = useRef(new AbortController()).current;
  const navigate = useNavigate();

  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const currency = currencies.find(c => c.currencyStr === 'EUR')!;

  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: -2,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [list, setList] = useState<IHelpdeskContract[]>([]);
  const [menuOpened, setMenuOpened] = useState(false);
  const [menuCoords, setMenuCoords] = useState<{ x: number; y: number } | null>(null);
  const [contextContractId, setContextContractId] = useState<number | null>(null);
  const [filterBarData, setFilterBarData] = useState<HelpdeskFilterBarFieldsData>();
  const [newContract, setNewContract] = useState<boolean>(false);

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadContractsList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: HelpdeskFilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadContractsList = () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setList([]);
    setTotalRows(0);

    fetchHelpdeskContracts(controller, offset, limit, true, filterBarData?.entity, filterBarData?.contractId)
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setList(response?.data?.list || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRowRightClick = (event: React.MouseEvent, selectedContract: number) => {
    event.preventDefault();
    setContextContractId(selectedContract);

    const menuWidth = 200;
    const menuHeight = 300;
    const maxX = window.innerWidth - menuWidth;
    const maxY = window.innerHeight - menuHeight;

    const x = event.clientX > maxX ? maxX : event.clientX;
    const y = event.clientY > maxY ? maxY : event.clientY;

    setMenuCoords({ x, y });
    setMenuOpened(true);
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const toggleNewContract = () => {
    setNewContract(!newContract);
    navigate(`/helpdesk/contract/new/0`);
  };

  return (
    <div className="helpdesk-contracts">
      <Header pageTitle={lang.HELPDESK_CONTRACTS_LABEL} />

      <HelpdeskFilterBar
        onSearch={loadContractsList}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          entity: true,
          contractId: true
        }}
        addButton={toggleNewContract}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.HELPDESK_CONTRACTS_ID}</Table.Th>
              <Table.Th>Entid</Table.Th>
              <Table.Th>EquipType</Table.Th>
              <Table.Th>{lang.HELPDESK_CONTRACTS_CREATEDATE}</Table.Th>
              <Table.Th>{lang.HELPDESK_CONTRACTS_VALUE}</Table.Th>
              <Table.Th>{lang.HELPDESK_CONTRACTS_STATE}</Table.Th>
              <Table.Th>{lang.HELPDESK_CONTRACTS_RENEWDATE}</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={16}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {!list.length ? (
                  <Table.Tr>
                    <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  list?.map((contract, index) => (
                    <Table.Tr
                      key={index}
                      onContextMenu={event => handleRowRightClick(event, contract.agreeId)}
                      onClick={() => navigate(`/helpdesk/contract/read/${contract.agreeId}`)}
                      style={{ cursor: 'context-menu' }}
                    >
                      <Table.Td>{contract.agreeId}</Table.Td>
                      <Table.Td>{contract.entid}</Table.Td>
                      <Table.Td>{contract.equiType}</Table.Td>
                      <Table.Td>{formatDateTimeLabel(contract.date || new Date().toDateString(), region)}</Table.Td>
                      <Table.Td>{formatCurrAmount(contract.value || 0, currency, false, true)}</Table.Td>
                      <Table.Td>{detectContractState(contract.state, lang)}</Table.Td>
                      <Table.Td>
                        {formatDateTimeLabel(contract.renovDate || new Date().toDateString(), region)}
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {menuOpened && menuCoords && (
        <Menu
          opened={true}
          onClose={() => setMenuOpened(false)}
          styles={{
            dropdown: {
              position: 'absolute',
              top: `${menuCoords.y}px`,
              left: `${menuCoords.x}px`
            }
          }}
        >
          <Menu.Target>
            <div
              style={{
                position: 'absolute',
                top: `${menuCoords.y}px`,
                left: `${menuCoords.x}px`,
                width: 1,
                height: 1
              }}
            />
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={() => navigate(`/helpdesk/contract/edit/${contextContractId}`)}>
              {lang.HELPDESK_CONTRACTS_EDIT}
            </Menu.Item>
            <Menu.Item onClick={() => navigate(`/helpdesk/contract/copyBy/${contextContractId}`)}>
              {lang.HELPDESK_CONTRACTS_COPY}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      )}

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default HelpdeskContracts;
